import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TablePagination,
  Paper,
  Divider,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as InvoiceIcon,
  ArrowBack as BackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  CompareArrows as MatchIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import {
  getInvoices,
  getInvoice,
  createInvoice,
  approveInvoice,
  rejectInvoice,
  performThreeWayMatching,
  linkInvoicePo,
  getSuppliers,
  getPurchaseOrders,
  formatCurrency,
  formatDate,
  getStatusColor,
  daysUntil
} from '../../services/procurementService';
import { StatusBadge, PageHeader, ActionBar, Timeline } from '../ds';
import { useEntityTimeline } from '../../services/auditTimelineService';
import { useListShortcuts } from '../common/GlobalShortcuts';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  Description as PdfIcon
} from '@mui/icons-material';
import { useBrand } from '../../context/BrandContext';
import SupplierPicker from '../common/SupplierPicker';
import ConfirmDialog from '../common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';

const INVOICE_STATUSES = ['pending', 'matched', 'approved', 'paid', 'rejected'];

function Invoices() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedBrand } = useBrand();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // List view state
  const [invoices, setInvoices] = useState([]);
  // Shared confirm + unsaved-changes guard for the create-invoice dialog,
  // which previously discarded a part-typed invoice on any backdrop click.
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const [formTouched, setFormTouched] = useState(false);
  const guardedCloseDialog = useUnsavedChangesGuard(formTouched, confirm);
  // Server-reported row count for the whole filtered set (not just this page).
  const [totalInvoices, setTotalInvoices] = useState(0);
  // Typing buffer: the TextField writes here on every keystroke and a debounced
  // effect mirrors it into searchTerm, which is what triggers the API call.
  // Without the buffer every letter would fire a request + spinner.
  const [searchInput, setSearchInput] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail view state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [matchingResult, setMatchingResult] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  // A freshly-opened dialog is never dirty. Must sit AFTER the dialogOpen
  // declaration above — the dependency array is read during render.
  useEffect(() => { if (dialogOpen) setFormTouched(false); }, [dialogOpen]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [invoiceForm, setInvoiceForm] = useState({
    supplier_id: '',
    po_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    payment_terms: 'Net 30',
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }]
  });

  // PO linking. Candidate POs are always scoped to ONE supplier — the backend rejects a
  // PO from a different supplier, and showing all 438 POs would invite exactly that mistake.
  const [poOptions, setPoOptions] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [linkInvoice, setLinkInvoice] = useState(null); // invoice being linked (list view)
  const [linkPo, setLinkPo] = useState(null);
  const [linkSaving, setLinkSaving] = useState(false);

  // Most invoices here are services/logistics (UPS, DHL, rent) that correctly have NO PO —
  // only the minority bought against a PO should ever show one. Without this filter the
  // "Link PO" button looks identical on ~1,477 blank rows and there's no way to tell which
  // ones actually need a link. "Needs PO" = no PO yet AND the supplier is one we genuinely
  // raise POs with. Resolved server-side (needs_po param) — see loadInvoices.
  const [needsPoOnly, setNeedsPoOnly] = useState(false);

  // Cache PO lists per supplier. Bulk-linking walks many invoices from the same supplier;
  // without this, each dialog open refetches the same list.
  const poCacheRef = useRef({});

  // Load the POs belonging to one supplier, newest first (the likely match is recent).
  // slim=true skips the PO list's receipt/payment/sku aggregate JOINs — we only need to
  // identify a PO here, and the full shape costs ~629 MiB peak per call on the server.
  const loadPosForSupplier = async (supplierId) => {
    if (!supplierId) { setPoOptions([]); return; }
    const cached = poCacheRef.current[supplierId];
    if (cached) { setPoOptions(cached); return; }
    setPoLoading(true);
    try {
      const res = await getPurchaseOrders({ supplier_id: supplierId, limit: 100, slim: true });
      const d = res?.data;
      const list = Array.isArray(d) ? d : (d?.purchase_orders || []);
      poCacheRef.current[supplierId] = list;
      setPoOptions(list);
    } catch (err) {
      console.error('Failed to load purchase orders:', err);
      setPoOptions([]);
    } finally {
      setPoLoading(false);
    }
  };


  const poOptionLabel = (po) =>
    po ? `${po.po_number} — ${formatDate(po.po_date)} — ${formatCurrency(po.total_amount, po.currency)}${po.status ? ` (${po.status})` : ''}` : '';

  const openLinkDialog = (invoice) => {
    setError(null); setLinkInvoice(invoice);
    setLinkPo(null);
    loadPosForSupplier(invoice.supplier_id);
  };

  // The backend hard-rejects a cross-currency link (PO_CURRENCY_MISMATCH) because the cash-flow
  // projection nets invoice totals against PO milestones with no FX conversion. So don't OFFER a
  // choice the server will refuse — only show POs in the invoice's own currency.
  const linkPoOptions = linkInvoice
    ? poOptions.filter((po) =>
        String(po.currency || '').toUpperCase() === String(linkInvoice.currency || '').toUpperCase())
    : poOptions;

  const handleLinkPo = async () => {
    if (!linkInvoice || !linkPo) return;
    setLinkSaving(true);
    try {
      setError(null);
      await linkInvoicePo(linkInvoice.id, linkPo.id);
      setSuccess(`Linked ${linkInvoice.invoice_number} to PO ${linkPo.po_number}`);
      // This supplier's cached PO list is now stale. (The "Needs PO" queue refreshes itself:
      // loadInvoices below re-runs the server-side query, and the linked invoice drops out.)
      delete poCacheRef.current[linkInvoice.supplier_id];
      setLinkInvoice(null);
      setLinkPo(null);
      if (id) { loadInvoiceDetails(id); } else { loadInvoices(); }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to link purchase order');
    } finally {
      setLinkSaving(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
    if (id) {
      loadInvoiceDetails(id);
    } else {
      // Every one of these is now a server-side query param, so each must
      // refetch: needsPoOnly, the filters, the search term and the page.
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedBrand?.id, needsPoOnly, searchTerm, statusFilter, supplierFilter, page, rowsPerPage]);

  // Debounce searchInput → searchTerm (400ms after the operator stops typing).
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any change to the filters or the search resets to page 1 — otherwise a
  // search made while on page 4 lands on page 4 of the new, shorter result set
  // and looks empty.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [searchTerm, statusFilter, supplierFilter, needsPoOnly]);

  const loadSuppliers = async () => {
    try {
      // include_non_po=true so the invoice supplier filter can reach vendors,
      // consultants, employees and contractors — not just PO suppliers
      // (product/raw_material/packaging). listSuppliers hides those four types by
      // default (they flooded the procurement Suppliers list), but ~149 active
      // non-intercompany invoice suppliers (DHL Ecommerce, TGR Logistics, Synergy
      // Retail Support, …) live in them and span ~1,850 invoices — filtering by
      // them was impossible with the default scope. Intercompany stays hidden.
      // limit high enough to return the whole active set in one page: the picker
      // (SupplierPicker) is a client-side searchable Autocomplete, so it needs all
      // options loaded, and the flat limit:100 silently truncated the list.
      const res = await getSuppliers({ is_active: true, include_non_po: true, limit: 1000 });
      // Handle response structure
      const suppliersData = res?.data;
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fully server-side: search, status, supplier and pagination all resolve
      // in SQL. This used to fetch a flat 100 rows and filter/paginate in the
      // browser — with ~1,500 invoices that made 93% of them unsearchable, so
      // an invoice on page 2 reported "No invoices found". Same shape the
      // goods-receipts list already uses.
      // Note: Procurement operates at company level, not brand level
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (supplierFilter) params.supplier_id = supplierFilter;
      // The "needs PO" backlog is resolved SERVER-side (see listInvoices). Filtering it in the
      // browser would only ever search the page we happened to load, hiding most of the backlog.
      if (needsPoOnly) params.needs_po = true;

      const res = await getInvoices(params);
      // Handle response structure
      const invoicesData = res?.data;
      const rows = Array.isArray(invoicesData) ? invoicesData : (invoicesData?.invoices || []);
      setInvoices(rows);
      // Server `total` drives the pager. Falling back to rows.length would make
      // the pager claim there is exactly one page.
      setTotalInvoices(
        Array.isArray(invoicesData) ? rows.length : Number(invoicesData?.total ?? rows.length)
      );
    } catch (err) {
      console.error('Failed to load invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Only the newest invoice load may apply its answer: an older one landing late (the user opened another invoice, or went
  // back to the list) would show the wrong invoice under this URL (DT-1139, same as Transfers DT-1113).
  const detailRequestRef = useRef(0);
  useEffect(() => { if (!id) detailRequestRef.current += 1; }, [id]);

  const loadInvoiceDetails = async (invoiceId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    // A different record: drop the previous one now, so a failed load never leaves it on screen under this URL.
    setSelectedInvoice(prev => (prev && String(prev.id) === String(invoiceId) ? prev : null));
    setLoading(true);
    setError(null);

    try {
      const res = await getInvoice(invoiceId);
      if (stale()) return;
      setSelectedInvoice(res?.data);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load invoice details:', err);
      setError('Failed to load invoice details. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  const handleAddItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const handleUpdateItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const calculateTotal = () => {
    return invoiceForm.items.reduce((total, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return total + (qty * price);
    }, 0);
  };

  const handleSave = async () => {
    try {
      setError(null);
      // po_id is optional. It must be OMITTED rather than sent as '' — the route
      // validates `body('po_id').optional().isInt()`, and an empty string is not
      // `undefined`, so it would fail validation instead of being skipped.
      const payload = { ...invoiceForm };
      if (!payload.po_id) delete payload.po_id;
      await createInvoice(payload);
      setSuccess('Invoice created successfully');
      setFormTouched(false); // saved — closing is safe now
      setDialogOpen(false);
      loadInvoices();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create invoice');
    }
  };

  const handleMatch = async (invoiceId) => {
    try {
      const res = await performThreeWayMatching(invoiceId);
      setMatchingResult(res?.data);
      setSuccess('Matching completed');
      loadInvoiceDetails(invoiceId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to perform matching');
    }
  };

  const handleApprove = async (invoiceId) => {
    try {
      await approveInvoice(invoiceId, 1); // TODO: Use actual user ID
      setSuccess('Invoice approved for payment');
      loadInvoiceDetails(invoiceId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to approve invoice');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    try {
      await rejectInvoice(selectedInvoice.id, rejectReason);
      setSuccess('Invoice rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      loadInvoiceDetails(selectedInvoice.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reject invoice');
    }
  };

  // `invoices` is already exactly what should be shown: the search, the status
  // and supplier filters, "Needs PO" and the pagination are all applied
  // server-side. No client-side filtering — that was the bug (it could only
  // ever search the rows that happened to be loaded).

  // Activity timeline — must be called unconditionally (rules-of-hooks).
  const { events: invoiceTimelineEvents, loading: invoiceTimelineLoading } = useEntityTimeline(
    'procurement_invoices_route',
    selectedInvoice?.id || null
  );

  // List-page shortcuts: `n` opens new invoice when on list page.
  useListShortcuts({
    onNew: () => { if (!id) { setError(null); setDialogOpen(true); } },
    onRefresh: () => { if (!id) loadInvoices(); },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail View
  if (id && selectedInvoice) {
    const daysToDue = daysUntil(selectedInvoice.due_date);
    // "Overdue" must key off the BALANCE, not the due date alone — a fully-paid invoice
    // whose due date has passed is NOT overdue (Rishabh 2026-07-23: Search showed "paid"
    // but this page said "338 days overdue"). status can still read 'approved' on a
    // fully-paid invoice, so use the balance (paid_amount vs total), not the status.
    const balanceDue = (Number(selectedInvoice.total_amount) || 0) - (Number(selectedInvoice.paid_amount) || 0);
    const isPaid = balanceDue <= 0.005;
    const isOverdue = daysToDue !== null && daysToDue < 0 && !isPaid;
    // QB single-total invoice: no PDF AND no line-item rows → the left detail column is nearly
    // empty, so stack the cards full-width instead of the wide-left/tall-right split (fixes the
    // big blank gap; Rishabh 2026-07-27).
    const isQbSingleTotal = !selectedInvoice.pdf_url && (selectedInvoice.items || []).length === 0;

    return (
      <Box>
        <PageHeader
          backTo={true} /* history -1: return to wherever they came from (e.g. Finance Search),
                            not always the Invoices list — Rishabh 2026-07-23 */
          primary={selectedInvoice.supplier_name || `Invoice ${selectedInvoice.invoice_number}`}
          slug={`Invoice ${selectedInvoice.invoice_number}`}
          metadata={[
            ...(selectedInvoice.po_number ? [{ label: `PO ${selectedInvoice.po_number}` }] : []),
            ...(selectedInvoice.total_amount != null ? [{
              label: formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency),
            }] : []),
            ...(selectedInvoice.due_date ? [{
              label: isPaid
                ? `Paid${selectedInvoice.paid_date ? ` ${formatDate(selectedInvoice.paid_date)}` : ''}`
                : (isOverdue ? `Overdue (was due ${formatDate(selectedInvoice.due_date)})` : `Due ${formatDate(selectedInvoice.due_date)}`),
            }] : []),
          ]}
          statusBadge={
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <StatusBadge status={selectedInvoice.status} size="medium" />
              {isPaid && selectedInvoice.status !== 'paid' && <StatusBadge status="paid" size="medium" />}
              {isOverdue && <StatusBadge status="overdue" size="medium" />}
            </Box>
          }
          actions={
            <ActionBar
              // View PDF is the primary action (a real button) when we have the stored document —
              // Rishabh 2026-07-27: the invoice PDF must be reachable from the opened invoice, not
              // just the timeline icon. Dropped "Copy invoice #" (low value) per the same feedback.
              primaryCount={selectedInvoice.pdf_url ? 1 : 0}
              actions={[
                ...(selectedInvoice.pdf_url ? [{
                  key: 'view-pdf',
                  label: 'View PDF',
                  icon: <PdfIcon fontSize="small" />,
                  onClick: () => window.open(selectedInvoice.pdf_url, '_blank', 'noopener'),
                }] : []),
                { key: 'print', label: 'Print', icon: <PrintIcon fontSize="small" />, onClick: () => window.print() },
                {
                  key: 'email-supplier',
                  label: 'Email supplier',
                  icon: <EmailIcon fontSize="small" />,
                  disabled: !selectedInvoice.supplier_name,
                  onClick: () => {
                    const subject = encodeURIComponent(`Invoice ${selectedInvoice.invoice_number || ''}`);
                    const body = encodeURIComponent(`Hi,\n\nRegarding invoice ${selectedInvoice.invoice_number || ''}${selectedInvoice.po_number ? ` (PO ${selectedInvoice.po_number})` : ''}.\n\nThanks,`);
                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  },
                },
              ]}
            />
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Action Buttons */}
        {selectedInvoice.status === 'pending' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MatchIcon />}
                  onClick={() => handleMatch(selectedInvoice.id)}
                >
                  Perform 3-Way Matching
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {selectedInvoice.status === 'matched' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={() => handleApprove(selectedInvoice.id)}
                >
                  Approve for Payment
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => { setError(null); setRejectDialogOpen(true); }}
                >
                  Reject Invoice
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {selectedInvoice.status === 'approved' && !isPaid && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/procurement/payments?invoice_id=${selectedInvoice.id}`)}
              >
                Record Payment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Invoice Details */}
        {/* A QB single-total invoice (no PDF, no line items — e.g. INV44649) has almost nothing
            in the left column, so the wide-left/tall-right 8/4 split leaves a big empty gap under
            the small Summary card (Rishabh 2026-07-27). In that case stack the cards full-width
            instead so there's no dead column; keep the 8/4 split when the left column has a PDF or
            a line-item table that needs the width. */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={isQbSingleTotal ? 12 : 8}>
            <Card>
              <CardContent>
                {(() => {
                  // QuickBooks imports many bills as a single header total with NO line-item
                  // rows (invoice_items empty). Rendering the Description/Qty/Unit-Price table
                  // then looks broken (empty body). So only show the itemized table when there
                  // ARE items; always show a clean totals summary; note when it's a QB total.
                  const items = selectedInvoice.items || [];
                  const hasLineItems = items.length > 0;
                  const summaryRow = (label, value, bold) => (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant={bold ? 'h6' : 'body1'} fontWeight={bold ? 700 : 400}>{label}</Typography>
                      <Typography variant={bold ? 'h6' : 'body1'} fontWeight={bold ? 700 : 400}
                                  color={bold ? 'primary' : 'text.primary'}>
                        {formatCurrency(value, selectedInvoice.currency)}
                      </Typography>
                    </Box>
                  );
                  return (
                    <>
                      {selectedInvoice.pdf_url ? (
                        // Show the actual invoice PDF inline — fills the space AND lets you read it
                        // without leaving the page (Rishabh 2026-07-27). "View PDF" button (top) opens
                        // it full-size / new tab as a fallback if the inline frame is blocked.
                        <>
                          <Typography variant="h6" fontWeight="600" gutterBottom>Invoice Document</Typography>
                          <Box component="iframe" title="Invoice PDF" src={selectedInvoice.pdf_url}
                               sx={{ width: '100%', height: 640, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
                        </>
                      ) : (
                        <>
                          <Typography variant="h6" fontWeight="600" gutterBottom>
                            {hasLineItems ? 'Line Items' : 'Summary'}
                          </Typography>
                          {hasLineItems && (
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="right">Quantity</TableCell>
                                    <TableCell align="right">Unit Price</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {items.map((item, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{item.description}</TableCell>
                                      <TableCell align="right">{item.quantity}</TableCell>
                                      <TableCell align="right">{formatCurrency(item.unit_price, selectedInvoice.currency)}</TableCell>
                                      <TableCell align="right">{formatCurrency(item.quantity * item.unit_price, selectedInvoice.currency)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                          {!hasLineItems && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Imported from QuickBooks as a single total — no line-item breakdown, and no PDF attached for this invoice.
                            </Typography>
                          )}
                        </>
                      )}
                      <Box sx={{ maxWidth: 380, ml: 'auto', mt: 1.5 }}>
                        {summaryRow('Subtotal', selectedInvoice.subtotal || selectedInvoice.total_amount)}
                        {selectedInvoice.tax_amount > 0 && summaryRow('Tax', selectedInvoice.tax_amount)}
                        <Divider sx={{ my: 0.5 }} />
                        {summaryRow('Total', selectedInvoice.total_amount, true)}
                      </Box>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={isQbSingleTotal ? 12 : 4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>Invoice Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Invoice Date</Typography>
                    <Typography fontWeight="medium">{formatDate(selectedInvoice.invoice_date)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Due Date</Typography>
                    <Typography fontWeight="medium" color={isOverdue ? 'error.main' : 'text.primary'}>
                      {formatDate(selectedInvoice.due_date)}
                      {daysToDue !== null && !isPaid && (
                        <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                          ({daysToDue >= 0 ? `${daysToDue} days` : `${Math.abs(daysToDue)} days overdue`})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Payment Terms</Typography>
                    <Typography fontWeight="medium">{selectedInvoice.payment_terms || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Currency</Typography>
                    <Typography fontWeight="medium">{selectedInvoice.currency || 'GBP'}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Amount Paid</Typography>
                    <Typography fontWeight="medium" color="success.main">
                      {formatCurrency(selectedInvoice.paid_amount || 0, selectedInvoice.currency)}
                    </Typography>
                  </Box>
                  {selectedInvoice.paid_date && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Paid Date</Typography>
                      <Typography fontWeight="medium">{formatDate(selectedInvoice.paid_date)}</Typography>
                    </Box>
                  )}
                  {selectedInvoice.paid_from && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Paid From</Typography>
                      <Typography fontWeight="medium">{selectedInvoice.paid_from}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Balance Due</Typography>
                    <Typography fontWeight="bold" color={isOverdue ? 'error.main' : 'primary.main'}>
                      {formatCurrency(
                        (selectedInvoice.total_amount || 0) - (selectedInvoice.paid_amount || 0),
                        selectedInvoice.currency
                      )}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {selectedInvoice.notes && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>Notes</Typography>
                  <Typography>{selectedInvoice.notes}</Typography>
                </CardContent>
              </Card>
            )}

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>Activity</Typography>
                <Timeline
                  events={invoiceTimelineEvents.map(e => ({
                    ...e,
                    timestamp: e.timestamp ? formatDate(e.timestamp) + ' ' + (String(e.timestamp).split(' ')[1] || '').slice(0, 5) : '',
                  }))}
                  loading={invoiceTimelineLoading}
                  empty={<Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>No activity yet.</Typography>}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject Invoice</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography gutterBottom>
              Please provide a reason for rejecting this invoice.
            </Typography>
            <TextField
              label="Rejection Reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleReject}>
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // List View
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Supplier Invoices
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage supplier invoices and 3-way matching
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setError(null); setDialogOpen(true); }}
          >
            New Invoice
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadInvoices}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search invoice no., supplier or PO..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <SupplierPicker
                value={supplierFilter}
                onChange={(sid) => setSupplierFilter(sid)}
                suppliers={suppliers}
                includeAllOption
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {INVOICE_STATUSES.map(status => (
                    <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" onClick={loadInvoices} fullWidth>
                Apply Filters
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Tooltip title="Show only invoices with no PO linked, from suppliers we actually raise POs with. Services and logistics invoices (couriers, rent, software) correctly have no PO and are hidden.">
                <FormControlLabel
                  control={
                    <Switch
                      checked={needsPoOnly}
                      onChange={(e) => {
                        // Refetch from the server — the filter is a server-side query, not a
                        // client-side mask over the page we already have.
                        setNeedsPoOnly(e.target.checked);
                        setPage(0);
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" color="textSecondary">
                      Needs PO (goods suppliers missing a PO link)
                    </Typography>
                  }
                />
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>PO Reference</TableCell>
                  <TableCell>Invoice Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices
                  .map((invoice) => {
                    const daysToDue = daysUntil(invoice.due_date);
                    // overdue only when there's still a balance due (not just status!='paid':
                    // a fully-paid invoice can still read status 'approved') — Rishabh 2026-07-23.
                    const isOverdue = daysToDue !== null && daysToDue < 0
                      && ((Number(invoice.total_amount) || 0) - (Number(invoice.paid_amount) || 0)) > 0.005;
                    return (
                      <TableRow
                        key={invoice.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/procurement/invoices/${invoice.id}`)}
                      >
                        <TableCell>
                          <Box
                            component="a"
                            onClick={(e) => { e.stopPropagation(); navigate(`/procurement/invoices/${invoice.id}`); }}
                            sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {invoice.invoice_number}
                          </Box>
                        </TableCell>
                        <TableCell>{invoice.supplier_name}</TableCell>
                        <TableCell>
                          {invoice.po_number ? (
                            invoice.po_number
                          ) : (
                            // No PO linked. For services/logistics that's correct and
                            // permanent; for goods suppliers it means the link is simply
                            // missing (QuickBooks carries no PO reference), so offer to attach one.
                            <Button
                              size="small"
                              startIcon={<LinkIcon fontSize="small" />}
                              onClick={(e) => { e.stopPropagation(); openLinkDialog(invoice); }}
                              sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0, px: 0.5 }}
                            >
                              Link PO
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                        <TableCell>
                          <Typography color={isOverdue ? 'error.main' : 'text.primary'}>
                            {formatDate(invoice.due_date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(invoice.total_amount, invoice.currency)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <StatusBadge status={invoice.status} />
                            {isOverdue && <Chip label="Overdue" size="small" color="error" />}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 4 }}>
                        <InvoiceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography color="textSecondary">
                          No invoices found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalInvoices}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => guardedCloseDialog(() => { setFormTouched(false); setDialogOpen(false); })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>New Supplier Invoice</DialogTitle>
        {/* onChangeCapture marks the form dirty so closing warns first. */}
        <DialogContent onChangeCapture={() => setFormTouched(true)}>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SupplierPicker
                  value={invoiceForm.supplier_id}
                  onChange={(sid) => {
                    // Changing supplier invalidates any PO already chosen (a PO belongs
                    // to exactly one supplier; the backend rejects a cross-supplier link).
                    setInvoiceForm({ ...invoiceForm, supplier_id: sid, po_id: '' });
                    loadPosForSupplier(sid);
                  }}
                  suppliers={suppliers}
                  label="Supplier"
                  required
                  size="medium"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Invoice Number"
                  value={invoiceForm.invoice_number}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  options={poOptions}
                  loading={poLoading}
                  disabled={!invoiceForm.supplier_id}
                  getOptionLabel={poOptionLabel}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={poOptions.find((p) => String(p.id) === String(invoiceForm.po_id)) || null}
                  onChange={(_, v) => { setFormTouched(true); setInvoiceForm({ ...invoiceForm, po_id: v ? v.id : '' }); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Purchase Order (optional)"
                      helperText={
                        !invoiceForm.supplier_id
                          ? 'Pick a supplier first'
                          : (poOptions.length === 0 && !poLoading
                              ? 'No purchase orders for this supplier — services/logistics invoices normally have none'
                              : 'Link this invoice to its PO to enable 3-way matching')
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <TextField
                  label="Invoice Date"
                  type="date"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  label="Due Date"
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={['Immediate', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90']}
                  value={invoiceForm.payment_terms || ''}
                  onChange={(_, v) => { setFormTouched(true); setInvoiceForm({ ...invoiceForm, payment_terms: v || '' }); }}
                  onInputChange={(_, v) => setInvoiceForm({ ...invoiceForm, payment_terms: v || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Payment Terms" placeholder="Pick or type custom" />
                  )}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Line Items</Typography>
            {invoiceForm.items.map((item, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      label="Description"
                      value={item.description}
                      onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Unit Price"
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleUpdateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                        disabled={invoiceForm.items.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Line Item
            </Button>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="h6" color="primary.contrastText">
                Total: {formatCurrency(calculateTotal())}
              </Typography>
            </Box>

            <TextField
              label="Notes"
              value={invoiceForm.notes}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => guardedCloseDialog(() => { setFormTouched(false); setDialogOpen(false); })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!invoiceForm.supplier_id || !invoiceForm.invoice_number}
          >
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link an existing invoice to its PO. This is the path for QuickBooks-imported
          bills, which arrive with no PO reference at all and can't be edited via the
          pending-only update route. */}
      <Dialog open={Boolean(linkInvoice)} onClose={() => setLinkInvoice(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Purchase Order</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Attach a PO to invoice <strong>{linkInvoice?.invoice_number}</strong> from{' '}
            <strong>{linkInvoice?.supplier_name}</strong>
            {linkInvoice?.total_amount != null && (
              <> ({formatCurrency(linkInvoice.total_amount, linkInvoice.currency)})</>
            )}
            .
          </Typography>
          <Autocomplete
            fullWidth
            options={linkPoOptions}
            loading={poLoading}
            getOptionLabel={poOptionLabel}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={linkPo}
            onChange={(_, v) => setLinkPo(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Purchase Order"
                autoFocus
                helperText={
                  poLoading
                    ? 'Loading purchase orders…'
                    : (poOptions.length === 0
                        ? 'This supplier has no purchase orders — if they supply services or logistics, no PO is expected.'
                        : (linkPoOptions.length === 0
                            ? `This supplier has POs, but none in ${linkInvoice?.currency || 'this invoice’s currency'} — a cross-currency link is rejected, as it would misstate the cash-flow projection.`
                            : `Only this supplier’s POs in ${linkInvoice?.currency || 'the invoice’s currency'} are shown. Match on amount and date.`))
                }
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkInvoice(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleLinkPo} disabled={!linkPo || linkSaving}>
            {linkSaving ? 'Linking…' : 'Link PO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Must be mounted for the unsaved-changes guard to work: the guard
          awaits a promise this dialog resolves. Without it, closing a dirty
          form would hang instead of prompting. */}
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}

export default Invoices;
